import {
  Component,
  AfterViewInit,
  OnInit,
  ViewChild,
  Input,
  OnDestroy,
  ElementRef,
  ChangeDetectorRef,
  NgZone
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTabGroup } from '@angular/material/tabs';
import { DeepIQService } from '../../iqservices/iq.service';
import { SideMenusService } from '../../core/side-menus/side-menus.service';
import { ChatFireService } from '../../gen2ai/chatfire.service';
import { Message } from '../../models/chat.model';
import * as showdown from 'showdown';
import jsPDF from 'jspdf';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import * as docx from 'docx';
import { saveAs } from 'file-saver';
import mkPacmanClient from 'pacman-client';
import { Observable, of, Subscription, merge } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { AIFireService } from '../../gen2ai/fire.service';
import { PalmyraX5IqService } from '../../services/palmyra-x5-iq.service';
import { aggressivelyFormatMarkdownForCodeBlocks } from '../../shared/markdown/aggressive-code-format';

/** Model tabs (Summary is rendered separately as tab index 0). */
type ModelId = 'mistraliq' | 'palmyrax5';

type TabStatus = 'idle' | 'success' | 'error';

interface PlaceholderOption {
  text: string;
  logoUrl: string;
  name: string;
}

@Component({
  selector: 'app-iq-eye',
  templateUrl: './iq-eye.component.html',
  styleUrls: ['./iq-eye.component.scss']
})
export class IqEyeComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatTabGroup) tabGroup!: MatTabGroup;
  @ViewChild('scrollRegion') private scrollRegion!: ElementRef<HTMLDivElement>;
  @ViewChild('userInputTextarea', { static: false }) textareaRef!: ElementRef<HTMLTextAreaElement>;

  prompt: string = '';
  inputText: string = '';
  submittedText: string = '';

  wordCount: number = 0;
  selectedTabIndex = 0;
  readonly selectedModels: ModelId[] = ['palmyrax5', 'mistraliq'];

  rawOutputs: (string | null)[] = [null, null];
  formattedOutputs: SafeHtml[] = ['', ''];
  formattedSubmittedWriting: SafeHtml = '';

  teamSummary: string | null = null;
  formattedSummary: SafeHtml = '';
  summaryError: string | null = null;
  summarySucceeded = false;

  /** Same landing treatment as multichat summary tab: rotating vendor logos + model name letters + typewriter tagline. */
  showLogo = true;
  logoKey = 0;
  showMainSubtitle = false;
  placeholderOptions: PlaceholderOption[] = [
    { text: 'Mistral delivers fast multilingual reasoning with strong consistency.', logoUrl: 'assets/logos/mistral.svg', name: 'Mistral' },
    { text: 'Writer X5 balances deep context, clarity, and efficient long-form analysis.', logoUrl: 'assets/logos/x5-baby.png', name: 'Writer X5' }
  ];
  currentOption: PlaceholderOption = this.placeholderOptions[0];
  logoFading = false;
  modelNameFading = false;
  currentPlaceholder = '';
  private placeholderIndex = 0;
  isShimmering = false;
  private shimmerDirection: 'teal-to-pink' | 'pink-to-teal' = 'teal-to-pink';
  private shimmerLetterIndex = -1;
  private shimmerLetters: string[] = [];
  private readonly typeSpeedMs = 120;
  private readonly backspaceSpeedMs = 100;
  private readonly pauseAfterTypeMs = 1500;
  private readonly pauseAfterDeleteMs = 500;
  private readonly shimmerStepMs = 70;
  private typingTimer: any = null;
  private headerTypingIndex = 0;
  private headerCharIndex = 0;
  private headerDeleting = false;
  private placeholderTimer: any = null;
  private shimmerTimer: any = null;
  private destroyed = false;
  private cycleToken = 0;
  readonly modelTabLabelColor = '#800042';
  trackByLetter = (_: number, item: { char: string; index: number }) => item.index;

  @Input() id!: string;
  currentMessages: Message[] = [];
  isLoading = false;
  userInput = '';
  @Input() summaryText: string = '';
  summaryHtml: string = '';

  loading: boolean = false;
  markdownOutput: string = '';
  error: string | null = null;

  tabStatuses: TabStatus[] = ['idle', 'idle'];

  private md = new showdown.Converter({
    tables: true,
    simplifiedAutoLink: true,
    strikethrough: true,
    tasklists: true
  });

  private prismLoaded = false;
  private prism: any = null;

  private hljsLoaded = false;
  private hljs: any = null;

  private requestsSub?: Subscription;
  private readonly minPromptWords = 100;

  readonly MODEL_CATALOG: Record<ModelId, string> = {
    mistraliq: 'Mistral',
    palmyrax5: 'Writer X5'
  };

  readonly MODEL_VENDOR_MAP: Record<ModelId, string> = {
    mistraliq: 'mistral',
    palmyrax5: 'cohere'
  };
  readonly summaryLogoSrc = 'assets/logos/iamclari2.png';

  private pacmanClient = mkPacmanClient({
    rockmanId: '9718dee6f1404543ba0c8ad27c5be405',
    serverUrl: 'https://de-pacman.sam-media.com/api/v2/mstore',
    impressionNumber: 1,
    signalInterval: 1000
  });

  constructor(
    private sideMenusService: SideMenusService,
    private chatService: ChatFireService,
    private sanitizer: DomSanitizer,
    private deep: DeepIQService,
    private fire: AIFireService,
    private palmyraX5: PalmyraX5IqService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    public snackBar: MatSnackBar
  ) {
    this.chatService.isLoading$.subscribe((loading) => {
      this.isLoading = loading;
      if (loading) {
        this.pacmanClient.sendFlowEvent({
          category: 'Flow',
          action: 'advance',
          label: 'loading'
        });
      }
    });
  }

  ngOnInit(): void {
    this.chatService.setChatHistoryNamespace('text').subscribe();

    if (this.id) {
      this.chatService.selectConversation(this.id);
    }

    this.chatService.currentConversation$.subscribe((conversation) => {
      if (conversation) {
        this.currentMessages = conversation.messages;
      }
    });

    this.showMainSubtitle = false;
    this.startPerfectPlaceholderCycle();
  }

  async ngAfterViewInit(): Promise<void> {
    await this.loadPrismIfNeeded();
    await this.loadHLJSIfNeeded();
    setTimeout(() => {
      this.highlightAll();
      this.highlightAllHLJS();
    }, 0);
  }

  ngOnDestroy(): void {
    this.requestsSub?.unsubscribe();
    this.stopPerfectPlaceholderCycle();
  }

  updateWordCount(): void {
    this.wordCount = this.inputText ? this.inputText.trim().split(/\s+/).length : 0;
  }

  onTabIndexChange(index: number): void {
    this.selectedTabIndex = index;
  }

  onPrimaryTextareaKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.onSubmit();
    }
  }

  onSubmit(): void {
    const prompt = (this.inputText || '').trim();
    if (!prompt) return;

    if (this.wordCount < this.minPromptWords) {
      this.snackBar.open(`Prompt must be at least ${this.minPromptWords} words.`, 'Close', {
        duration: 3000
      });
      return;
    }

    this.requestsSub?.unsubscribe();
    this.requestsSub = undefined;

    this.inputText = '';
    this.updateWordCount();

    this.prompt = prompt;
    this.submittedText = prompt;

    this.error = null;
    this.isLoading = true;
    this.loading = true;
    this.teamSummary = null;
    this.formattedSummary = '';
    this.summaryError = null;
    this.summarySucceeded = false;
    this.rawOutputs = [null, null];
    this.formattedOutputs = ['', ''].map((v) => this.asHtml(v));
    this.formattedSubmittedWriting = this.asHtml(prompt);
    this.resetTabStatuses();
    this.stopPerfectPlaceholderCycle();
    this.startPerfectPlaceholderCycle();

    const requests = [
      this.palmyraX5.analyzePromptAndWritingSample(prompt, prompt).pipe(
        take(1),
        catchError((err) => of(`(Writer X5) ${this.errString(err)}`)),
        map((text) => ({ idx: 0 as const, text }))
      ),
      this.deep.mistralLargeIQAssessment(prompt).pipe(
        take(1),
        map((r) => this.parseMistralLargeIQResponse(r)),
        catchError((err) => of(`(Mistral) ${this.errString(err)}`)),
        map((text) => ({ idx: 1 as const, text }))
      ),
    ];

    let pending = requests.length;
    let firstSuccessfulTabShown = false;

    this.requestsSub = merge(...requests).subscribe({
      next: ({ idx, text }) => {
        this.rawOutputs[idx] = text;
        this.formattedOutputs[idx] = this.asHtml(text);

        const isError = idx === 0 ? text.startsWith('(Writer X5)') : text.startsWith('(Mistral)');
        this.tabStatuses[idx] = isError ? 'error' : 'success';

        if (!firstSuccessfulTabShown && !isError) {
          firstSuccessfulTabShown = true;
          const tabIndex = idx + 1; // 0 is Summary tab
          this.selectedTabIndex = tabIndex;
          if (this.tabGroup) this.tabGroup.selectedIndex = tabIndex;
        }

        pending -= 1;
        if (pending > 0) {
          return;
        }

        const summaryInput: Record<string, string> = {
          palmyrax5: this.rawOutputs[0] || '',
          mistraliq: this.rawOutputs[1] || ''
        };

        this.fire.callCombinedSummary(prompt, summaryInput).pipe(take(1)).subscribe({
          next: (res) => {
            const summary = res && typeof res.summary === 'string' ? res.summary.trim() : '';
            this.summarySucceeded = !!summary;

            if (this.summarySucceeded) {
              this.teamSummary = summary;
              this.formattedSummary = this.asHtml(this.teamSummary);
              this.summaryError = null;
              this.stopPerfectPlaceholderCycle();
            } else {
              this.teamSummary = null;
              this.formattedSummary = '';
              this.summaryError = 'Summary returned no content.';
            }

            this.isLoading = false;
            this.loading = false;
            if (this.summarySucceeded) {
              this.selectedTabIndex = 0;
              if (this.tabGroup) this.tabGroup.selectedIndex = 0;
            }

            void this.loadPrismIfNeeded();
            void this.loadHLJSIfNeeded();
            setTimeout(() => {
              this.highlightAll();
              this.highlightAllHLJS();
            }, 0);
          },
          error: (err) => {
            this.teamSummary = null;
            this.formattedSummary = '';
            this.summarySucceeded = false;
            this.summaryError = `(Summary error) ${this.errString(err)}`;
            this.isLoading = false;
            this.loading = false;
            this.stopPerfectPlaceholderCycle();
          }
        });
      },
      error: (err) => {
        this.error = this.errString(err);
        this.isLoading = false;
        this.loading = false;
        this.stopPerfectPlaceholderCycle();
      }
    });
  }

  parseMistralLargeIQResponse(response: any): string {
    return this.parseDeepseekResponse(response);
  }

  parseClaude35Response(response: any): string {
    try {
      const data = typeof response === 'string' ? JSON.parse(response) : response;

      if (data?.model_output && typeof data.model_output === 'string') {
        return data.model_output;
      }

      if (data?.output?.message?.content && Array.isArray(data.output.message.content)) {
        const textContent = data.output.message.content.find((c: any) => c?.text);
        if (textContent?.text) {
          return textContent.text;
        }
      }

      if (Array.isArray(data?.content) && data.content.length > 0) {
        const text = data.content[0]?.text;
        if (text) {
          return text;
        }
      }

      if (typeof data?.completion === 'string') {
        return data.completion;
      }

      if (typeof data?.text === 'string') {
        return data.text;
      }

      if (Array.isArray(data?.choices) && data.choices.length > 0) {
        const content = data.choices[0]?.message?.content;
        if (content) {
          return content;
        }
      }

      return 'No Data';
    } catch (error) {
      console.error('Error parsing Sonnet 4 IQ response:', error);
      return 'Invalid response from Claude Sonnet 4';
    }
  }

  parseDeepSeekV32IQResponse(response: any): string {
    return this.parseDeepseekResponse(response);
  }

  parseMaverickIQResponse(response: any): string {
    return this.parseMaverickResponse(response);
  }

  parseMaverickResponse(response: any): string {
    try {
      const data = typeof response === 'string' ? JSON.parse(response) : response;

      if (data?.generation) {
        return data.generation;
      }

      if (data?.output?.message?.content && Array.isArray(data.output.message.content)) {
        const textContent = data.output.message.content.find((c: any) => c?.text);
        if (textContent?.text) return textContent.text;
      }

      if (data?.model_output) {
        return data.model_output;
      }

      if (data?.summary) {
        return data.summary;
      }

      return 'No Data';
    } catch (error) {
      console.error('Error parsing Maverick 4 response:', error);
      return 'Invalid response from Maverick 4';
    }
  }

  parsePalmxResponse(response: any): string {
    try {
      const data = typeof response === 'string' ? JSON.parse(response) : response;

      if (data?.model_output) {
        return data.model_output;
      }

      if (data?.generation) {
        return data.generation;
      }

      if (data?.content) {
        return data.content;
      }

      return 'No model output data';
    } catch (error) {
      console.error('Error parsing Palmx response:', error);
      return 'Invalid response from Palmx';
    }
  }

  parseDeepseekResponse(response: any): string {
    try {
      const data = typeof response === 'string' ? JSON.parse(response) : response;

      if (Array.isArray(data?.choices) && data.choices.length > 0) {
        const content = data.choices[0]?.message?.content;
        if (content) return content;
      }

      if (data?.model_output) {
        return data.model_output;
      }

      if (data?.content) {
        return data.content;
      }

      if (Array.isArray(data?.content) && data.content.length > 0) {
        const text = data.content[0]?.text;
        if (text) return text;
      }

      return 'No Data';
    } catch (error) {
      console.error('Error parsing DeepSeek IQ response:', error);
      return 'Invalid response from DeepSeek V3.2';
    }
  }

  private resetTabStatuses(): void {
    this.tabStatuses = this.selectedModels.map(() => 'idle');
  }

  private asHtml(markdown: string | null): SafeHtml {
    const aggressive = aggressivelyFormatMarkdownForCodeBlocks((markdown || '').toString());
    return this.sanitizer.bypassSecurityTrustHtml(this.md.makeHtml(aggressive));
  }

  private errString(err: any): string {
    if (!err) return 'Unknown error';
    if (typeof err === 'string') return err;
    if (err.message) return err.message;
    try {
      return JSON.stringify(err);
    } catch {
      return 'Error';
    }
  }

  private async loadPrismIfNeeded(): Promise<void> {
    if (this.prismLoaded) return;
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    const core = await import('prismjs');
    await Promise.all([
      import('prismjs/components/prism-markup'),
      import('prismjs/components/prism-markdown'),
      import('prismjs/components/prism-javascript'),
      import('prismjs/components/prism-typescript'),
      import('prismjs/components/prism-json'),
      import('prismjs/components/prism-css'),
      import('prismjs/components/prism-scss'),
      import('prismjs/components/prism-bash'),
      import('prismjs/components/prism-shell-session'),
      import('prismjs/components/prism-python'),
      import('prismjs/components/prism-java'),
      import('prismjs/components/prism-csharp'),
      import('prismjs/components/prism-cpp'),
      import('prismjs/components/prism-yaml'),
      import('prismjs/components/prism-sql')
    ]).catch(() => {});

    this.prism = (window as any).Prism || (core as any);
    this.prismLoaded = !!this.prism;
  }

  private async loadHLJSIfNeeded(): Promise<void> {
    if (this.hljsLoaded) return;
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    const mod = await import('highlight.js/lib/common');
    this.hljs = (mod as any).default || (mod as any);
    this.hljsLoaded = !!this.hljs;
  }

  private highlightAll(): void {
    try {
      if (!this.prismLoaded || !this.prism) return;
      const root = (this.scrollRegion?.nativeElement as HTMLElement) || document;
      if ((this.prism as any).highlightAllUnder) (this.prism as any).highlightAllUnder(root);
      else if ((this.prism as any).highlightAll) (this.prism as any).highlightAll();
    } catch {}
  }

  private highlightAllHLJS(): void {
    try {
      if (!this.hljsLoaded || !this.hljs) return;
      const root: HTMLElement = (this.scrollRegion?.nativeElement as HTMLElement) || document.body;
      const blocks = root.querySelectorAll('pre code:not([class*="language-"])');

      blocks.forEach((codeEl: Element) => {
        const pre = codeEl.parentElement as HTMLElement | null;
        if ((codeEl as HTMLElement).querySelector('.token') || (codeEl as HTMLElement).classList.contains('prism'))
          return;
        this.hljs.highlightElement(codeEl as HTMLElement);
        pre?.classList.add('hljs');
      });
    } catch {}
  }

  modelName(id: ModelId): string {
    return this.MODEL_CATALOG[id] || id;
  }

  tabLabel(id: ModelId): string {
    const shortNames: Record<ModelId, string> = {
      mistraliq: 'Mistral',
      palmyrax5: 'Writer X5'
    };
    return shortNames[id] || this.MODEL_CATALOG[id] || id;
  }

  getVendorLogo(modelId: ModelId): string | null {
    const vendor = this.MODEL_VENDOR_MAP[modelId];
    return vendor ? `assets/logos/${vendor}.svg` : null;
  }

  hasVendorLogo(modelId: ModelId): boolean {
    return !!this.MODEL_VENDOR_MAP[modelId];
  }

  placeholderHint(modelId: ModelId): string {
    switch (modelId) {
      case 'palmyrax5':
        return 'Writer X5 delivers structured IQ insights using your submitted writing patterns today.';
      case 'mistraliq':
        return 'Mistral delivers concise synthesis and broad trait coverage from your writing.';
      default:
        return '';
    }
  }

  get modelKimColor(): string {
    return this.modelTabLabelColor;
  }

  get modelNameClass(): string {
    return this.isShimmering ? `model-name shimmer-${this.shimmerDirection}` : 'model-name';
  }

  get modelNameLetters(): Array<{ char: string; index: number }> {
    const name = this.currentOption?.name || 'AI Model';
    return Array.from(name).map((char, index) => ({ char, index }));
  }

  getLetterColor(letterIndex: number): string {
    if (!this.isShimmering) return this.modelKimColor;

    const startColor = '#008080';
    const endColor = '#800042';

    if (letterIndex < this.shimmerLetterIndex) return endColor;
    return startColor;
  }

  isLetterWaving(letterIndex: number): boolean {
    return this.isShimmering && letterIndex === this.shimmerLetterIndex;
  }

  private clearPlaceholderTimer(): void {
    if (this.placeholderTimer) {
      clearTimeout(this.placeholderTimer);
      this.placeholderTimer = null;
    }
  }

  private clearShimmerTimer(): void {
    if (this.shimmerTimer) {
      clearTimeout(this.shimmerTimer);
      this.shimmerTimer = null;
    }
  }

  private clearTypingTimer(): void {
    if (this.typingTimer) {
      clearTimeout(this.typingTimer);
      this.typingTimer = null;
    }
  }

  private schedulePlaceholder(token: number, delayMs: number, fn: () => void): void {
    this.clearPlaceholderTimer();
    this.ngZone.runOutsideAngular(() => {
      this.placeholderTimer = setTimeout(() => {
        if (this.destroyed || token !== this.cycleToken) return;
        this.ngZone.run(() => fn());
      }, delayMs);
    });
  }

  private scheduleTyping(token: number, delayMs: number, fn: () => void): void {
    this.clearTypingTimer();
    this.ngZone.runOutsideAngular(() => {
      this.typingTimer = setTimeout(() => {
        if (this.destroyed || token !== this.cycleToken) return;
        this.ngZone.run(() => fn());
      }, delayMs);
    });
  }

  private scheduleShimmer(token: number, delayMs: number, fn: () => void): void {
    this.clearShimmerTimer();
    this.ngZone.runOutsideAngular(() => {
      this.shimmerTimer = setTimeout(() => {
        if (this.destroyed || token !== this.cycleToken) return;
        this.ngZone.run(() => fn());
      }, delayMs);
    });
  }

  private startPerfectPlaceholderCycle(): void {
    this.destroyed = false;
    this.cycleToken++;

    this.headerTypingIndex = 0;
    this.headerCharIndex = 0;
    this.headerDeleting = false;

    this.placeholderIndex = 0;
    this.currentOption = this.placeholderOptions[this.placeholderIndex];

    this.currentPlaceholder = '';

    this.logoFading = false;
    this.modelNameFading = false;

    this.stopShimmer();
    this.startShimmer(this.cycleToken);

    this.cdr.detectChanges();

    this.stepHeaderTypewriter(this.cycleToken);
  }

  private stepHeaderTypewriter(token: number): void {
    if (this.destroyed || token !== this.cycleToken) return;

    const option = this.placeholderOptions[this.headerTypingIndex];
    const fullText = option?.text ?? '';

    if (this.currentOption?.logoUrl !== option?.logoUrl) {
      this.showLogo = false;
      this.cdr.detectChanges();

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.logoKey++;
          this.currentOption = option;
          this.showLogo = true;

          this.stopShimmer();
          this.shimmerDirection = 'teal-to-pink';
          this.startShimmer(token);

          this.cdr.detectChanges();
        });
      });
    } else {
      this.currentOption = option;
    }

    this.placeholderIndex = this.headerTypingIndex;

    if (!this.headerDeleting) {
      this.headerCharIndex = Math.min(this.headerCharIndex + 1, fullText.length);
      this.currentPlaceholder = fullText.slice(0, this.headerCharIndex);
      this.cdr.detectChanges();

      if (this.headerCharIndex >= fullText.length) {
        this.headerDeleting = true;
        this.scheduleTyping(token, this.pauseAfterTypeMs, () => this.stepHeaderTypewriter(token));
      } else {
        this.scheduleTyping(token, this.typeSpeedMs, () => this.stepHeaderTypewriter(token));
      }
      return;
    }

    this.headerCharIndex = Math.max(this.headerCharIndex - 1, 0);
    this.currentPlaceholder = fullText.slice(0, this.headerCharIndex);
    this.cdr.detectChanges();

    if (this.headerCharIndex <= 0) {
      this.headerDeleting = false;
      this.headerTypingIndex = (this.headerTypingIndex + 1) % this.placeholderOptions.length;

      this.stopShimmer();
      this.shimmerDirection = 'teal-to-pink';
      this.startShimmer(token);

      this.scheduleTyping(token, this.pauseAfterDeleteMs, () => this.stepHeaderTypewriter(token));
    } else {
      this.scheduleTyping(token, this.backspaceSpeedMs, () => this.stepHeaderTypewriter(token));
    }
  }

  private stopPerfectPlaceholderCycle(): void {
    this.destroyed = true;
    this.cycleToken++;
    this.clearPlaceholderTimer();
    this.clearShimmerTimer();
    this.clearTypingTimer();
    this.stopShimmer();
  }

  private startShimmer(token: number): void {
    this.stopShimmer();
    this.isShimmering = true;

    this.shimmerLetters = Array.from(this.currentOption?.name || 'AI Model');
    this.shimmerLetterIndex = -1;

    this.stepShimmer(token);
  }

  private stepShimmer(token: number): void {
    if (this.destroyed || token !== this.cycleToken || !this.isShimmering) return;

    this.shimmerLetterIndex++;

    if (this.shimmerLetterIndex >= this.shimmerLetters.length) {
      this.stopShimmer();
      this.cdr.detectChanges();
      return;
    }

    this.cdr.detectChanges();
    this.scheduleShimmer(token, this.shimmerStepMs, () => this.stepShimmer(token));
  }

  private stopShimmer(): void {
    this.clearShimmerTimer();
    this.isShimmering = false;
    this.shimmerLetterIndex = -1;
  }

  exportToPDF(): void {
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const margin = 40;
    const width = doc.internal.pageSize.getWidth() - margin * 2;
    let y = margin;

    const add = (title: string, text: string | null) => {
      doc.setFont('Helvetica', 'bold');
      doc.setFontSize(14);
      doc.text(title, margin, y);
      y += 18;

      if (text) {
        doc.setFont('Helvetica', 'normal');
        doc.setFontSize(11);
        const lines = doc.splitTextToSize(text, width);
        lines.forEach((line: string) => {
          if (y > doc.internal.pageSize.getHeight() - margin) {
            doc.addPage();
            y = margin;
          }
          doc.text(line, margin, y);
          y += 14;
        });
      }
      y += 10;
    };

    add('Summary', this.teamSummary || '');
    this.selectedModels.forEach((id, i) => {
      add(this.MODEL_CATALOG[id], this.rawOutputs[i] || '');
    });

    doc.save('iq-summary.pdf');
  }

  exportToDOCX(): void {
    const createParagraphs = (title: string, content: string): docx.Paragraph[] => {
      const paragraphs: docx.Paragraph[] = [];
      paragraphs.push(
        new docx.Paragraph({
          text: title,
          heading: docx.HeadingLevel.HEADING_2,
          spacing: { after: 300 }
        })
      );

      const splitSentences = (content || '').split('\n');
      splitSentences.forEach((sentence) => {
        paragraphs.push(
          new docx.Paragraph({
            text: sentence.trim(),
            spacing: { after: 200 },
            style: 'Normal',
            indent: { left: 720 }
          })
        );
      });

      return paragraphs;
    };

    const document = new docx.Document({
      sections: [
        {
          properties: {},
          children: [
            ...createParagraphs('Summary', this.teamSummary || ''),
            ...this.selectedModels
              .map((id, i) => createParagraphs(this.MODEL_CATALOG[id], this.rawOutputs[i] || ''))
              .flat()
          ]
        }
      ]
    });

    docx.Packer.toBlob(document).then((blob) => {
      saveAs(blob, 'iq-summary.docx');
    });
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  sendMessage(): void {
    if (!this.userInput.trim() || this.isLoading) return;
    const message = this.userInput;
    this.userInput = '';
    this.chatService.sendMessage(message);
  }
}
